// ============================================================
// APPLICATIONS ADMIN HOOK - CLIENT-SIDE FILTERING CACHE
// ============================================================
// Strategy: Load all applications once, cache in memory, filter client-side

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Application } from '../features/applications/types';
import { api } from '../services/api';

export interface ApplicationsAdminState {
  applications: Application[];
  filteredApplications: Application[];
  allApplications: Application[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  stats: {
    total: number;
    new: number;
    reviewing: number;
    approved: number;
    rejected: number;
    closed: number;
  };
  filteredStats?: {
    total: number;
    new: number;
    reviewing: number;
    approved: number;
    rejected: number;
    closed: number;
  };
}

export interface UseApplicationsAdminReturn extends ApplicationsAdminState {
  statusFilter: string;
  searchQuery: string;
  updateFilter: (key: string, value: string) => void;
  updateSearch: (query: string) => void;
  resetFilters: () => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  refresh: () => Promise<void>;
}

const DEFAULT_PAGE_SIZE = 10;

export function useApplicationsAdmin(): UseApplicationsAdminReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const [allApplications, setAllApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const statusFilter = searchParams.get('status') ?? 'all';
  const searchQuery = searchParams.get('search') ?? '';
  const page = Number(searchParams.get('page') ?? '1');
  const pageSize = Number(searchParams.get('pageSize') ?? DEFAULT_PAGE_SIZE);

  const fetchAllApplications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/applications/admin/all?page=1&pageSize=1000');
      if (!data.success) {
        throw new Error(data.error || 'Erreur lors de la récupération des candidatures');
      }
      const { items } = data.data;
      setAllApplications(items);
    } catch (err: any) {
      console.error('[useApplicationsAdmin] Fetch error:', err);
      setError(err.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }, []);

  // Chargement initial de toutes les candidatures
  useEffect(() => {
    fetchAllApplications();
  }, [fetchAllApplications]);

  // Filtrage client-side
  const filteredApplications = useMemo(() => {
    let result = allApplications;

    // Filtre par statut
    if (statusFilter !== 'all') {
      if (statusFilter === 'APPROVED') {
        // "Approuvée" inclut à la fois APPROVED et IN_PROGRESS
        result = result.filter((a) => a.status === 'APPROVED' || a.status === 'IN_PROGRESS');
      } else {
        result = result.filter((a) => a.status === statusFilter);
      }
    }

    // Recherche textuelle
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.ref.toLowerCase().includes(query) ||
          a.candidate.toLowerCase().includes(query) ||
          a.space.toLowerCase().includes(query),
      );
    }

    return result;
  }, [allApplications, statusFilter, searchQuery]);

  // Pagination côté client
  const totalPages = Math.ceil(filteredApplications.length / pageSize);
  const startIndex = (page - 1) * pageSize;
  const paginatedApplications = filteredApplications.slice(startIndex, startIndex + pageSize);

  // Stats GLOBALES (toutes les candidatures)
  const globalStats = useMemo(
    () => ({
      total: allApplications.length,
      new: allApplications.filter((a) => a.status === 'NEW').length,
      reviewing: allApplications.filter((a) => a.status === 'REVIEWING').length,
      // "Approuvées" inclut APPROVED (en attente de paiement) + IN_PROGRESS (paiements en cours)
      approved: allApplications.filter((a) => a.status === 'APPROVED' || a.status === 'IN_PROGRESS').length,
      rejected: allApplications.filter((a) => a.status === 'REJECTED').length,
      closed: allApplications.filter((a) => a.status === 'CLOSED').length,
    }),
    [allApplications],
  );

  // Stats FILTRÉES (pour usage interne si besoin)
  const filteredStats = useMemo(
    () => ({
      total: filteredApplications.length,
      new: filteredApplications.filter((a) => a.status === 'NEW').length,
      reviewing: filteredApplications.filter((a) => a.status === 'REVIEWING').length,
      // "Approuvées" inclut APPROVED (en attente de paiement) + IN_PROGRESS (paiements en cours)
      approved: filteredApplications.filter((a) => a.status === 'APPROVED' || a.status === 'IN_PROGRESS').length,
      rejected: filteredApplications.filter((a) => a.status === 'REJECTED').length,
      closed: filteredApplications.filter((a) => a.status === 'CLOSED').length,
    }),
    [filteredApplications],
  );

  const updateFilter = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        if (value === 'all') {
          prev.delete(key);
        } else {
          prev.set(key, value);
        }
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  const updateSearch = useCallback(
    (query: string) => {
      setSearchParams((prev) => {
        if (query.trim()) {
          prev.set('search', query);
        } else {
          prev.delete('search');
        }
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  const resetFilters = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const setPage = useCallback(
    (page: number) => {
      setSearchParams((prev) => {
        prev.set('page', String(page));
        return prev;
      });
    },
    [setSearchParams],
  );

  const setPageSize = useCallback(
    (size: number) => {
      setSearchParams((prev) => {
        prev.set('pageSize', String(size));
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  return {
    applications: paginatedApplications,
    filteredApplications,
    allApplications,
    loading,
    error,
    pagination: {
      total: filteredApplications.length,
      page,
      pageSize,
      totalPages,
    },
    stats: globalStats, // Stats GLOBALES pour les KPI cards
    filteredStats, // Stats filtrées (optionnel)
    statusFilter,
    searchQuery,
    updateFilter,
    updateSearch,
    resetFilters,
    setPage,
    setPageSize,
    refresh: fetchAllApplications,
  };
}
